import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   DoCheck,
   Input,
   OnDestroy,
   OnInit,
   ViewEncapsulation,
} from '@angular/core';
import {
   ControlContainer,
   FormGroupDirective,
   NG_VALUE_ACCESSOR,
   UntypedFormArray,
   UntypedFormControl,
   UntypedFormGroup,
} from '@angular/forms';
import { ExtensionTypes, NullActionTypes } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { copyNameControlToShortNameAndDisplayName } from 'src/app/common-components/helpers/extension-form-helper';
import { ExtensionOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import { getNullActionPlaceholder, setNullActionValue } from '../../helpers/null-action-helper';
import { ExtensionTypeDetails } from '../../models/extension-type-details';
import { GetExtensionDetail_extension_extensionHuntGroup } from '../../queries/generated/GetExtensionDetail';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [{ multi: true, provide: NG_VALUE_ACCESSOR, useExisting: ExtensionHuntGroupDetailsComponent }],
    selector: 'ndt-extension-hunt-group-details',
    styleUrls: ['./extension-hunt-group-details.component.scss'],
    templateUrl: './extension-hunt-group-details.component.html',
    standalone: false
})
export class ExtensionHuntGroupDetailsComponent extends ExtensionTypeDetails implements OnInit, OnDestroy, DoCheck {
   @Input() extensionHuntGroup: GetExtensionDetail_extension_extensionHuntGroup | null;

   @Input() accountId: number | null;

   @Input() extensionId: number | null;

   @Input() extensions: [] | null;

   @Input() extensionGroups: [] | null;

   @Input() set canEdit(edit: boolean) {
      if (!edit) {
         this.form?.disable();
      } else {
         this.form?.enable();
      }
      this._canEdit = edit;
   }

   get canEdit(): boolean {
      return this._canEdit;
   }

   form: UntypedFormGroup;

   searchControl = new UntypedFormControl('');

   huntGroupExtensionTypes = [ExtensionTypes.Phone];

   extensionIdsToExclude: number[] = [];

   extensionGroupIdsToExclude: number[] = [];

   nullActions: NullActionTypes[] = [NullActionTypes.Directory, NullActionTypes.Hangup];

   routeBusyPlaceholder: string;

   routeNoAnswerPlaceholder: string;

   isRouteBusyExtensionTypePhone: boolean;

   isRouteNoAnswerExtensionTypePhone: boolean;

   get huntGroupMembers(): UntypedFormArray {
      const huntGroupMembers = this.form.get('extensionsOrExtensionGroupMembers') as UntypedFormArray;
      return huntGroupMembers;
   }

   private _canEdit: boolean;

   private destroyed$ = new Subject<void>();

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      private controlContainer: ControlContainer,
      private parentForm: FormGroupDirective
   ) {
      super();
   }

   ngOnDestroy(): void {
      this.extensionHuntGroup = null;
      this.destroyed$.next();
   }

   ngOnInit(): void {
      this.form = <UntypedFormGroup>this.controlContainer.control;
      this.extensionIdsToExclude = this.extension ? [this.extension.extensionId] : [];
      this.form
         .get('routeBusyExtension')
         ?.valueChanges.pipe(takeUntil(this.destroyed$))
         .subscribe((ext) => {
            this.isRouteBusyExtensionTypePhone = ext?.type === ExtensionTypes.Phone && ext.extensionId >= 0;
            if (!this.isRouteBusyExtensionTypePhone) {
               const routeBusyVoicemailOnly = this.form.get('isRouteBusyVoicemailOnly');
               routeBusyVoicemailOnly?.setValue(false);
               routeBusyVoicemailOnly?.markAsDirty();
               routeBusyVoicemailOnly?.updateValueAndValidity();
            }
         });
      this.form
         .get('routeNoAnswerExtension')
         ?.valueChanges.pipe(takeUntil(this.destroyed$))
         .subscribe((ext) => {
            this.isRouteNoAnswerExtensionTypePhone = ext?.type === ExtensionTypes.Phone && ext.extensionId >= 0;
            if (!this.isRouteNoAnswerExtensionTypePhone) {
               const routeNoAnswerVoicemailOnly = this.form.get('isRouteNoAnswerVoicemailOnly');
               routeNoAnswerVoicemailOnly?.setValue(false);
               routeNoAnswerVoicemailOnly?.markAsDirty();
               routeNoAnswerVoicemailOnly?.updateValueAndValidity();
            }
         });
      this.isRouteBusyExtensionTypePhone =
         this.extension?.extensionHuntGroup?.routeBusyExtension?.type === ExtensionTypes.Phone;
      this.isRouteNoAnswerExtensionTypePhone =
         this.extension?.extensionHuntGroup?.routeNoAnswerExtension?.type === ExtensionTypes.Phone;
      copyNameControlToShortNameAndDisplayName(this.parentForm.form, this.form);

      const routeBusyExtension = this.parentForm.form.get('extensionHuntGroup.routeBusyExtension');
      const routeBusyNullAction = this.parentForm.form.get('extensionHuntGroup.routeBusyNullAction');
      const routeNoAnswerExtension = this.parentForm.form.get('extensionHuntGroup.routeNoAnswerExtension');
      const routeNoAnswerNullAction = this.parentForm.form.get('extensionHuntGroup.routeNoAnswerNullAction');

      this.routeBusyPlaceholder = getNullActionPlaceholder(routeBusyNullAction?.value);
      this.routeNoAnswerPlaceholder = getNullActionPlaceholder(routeNoAnswerNullAction?.value);

      routeBusyNullAction?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
         this.routeBusyPlaceholder = getNullActionPlaceholder(value);
      });
      routeNoAnswerNullAction?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
         this.routeNoAnswerPlaceholder = getNullActionPlaceholder(value);
      });
      let lastRouteBusyValue = { current: null };
      routeBusyExtension?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value: ExtensionOption | null) => {
         setNullActionValue(value, lastRouteBusyValue, routeBusyExtension, routeBusyNullAction);
         this.routeBusyPlaceholder = getNullActionPlaceholder(routeBusyNullAction?.value);
      });
      let lastRouteNoAnswerValue = { current: null };
      routeNoAnswerExtension?.valueChanges
         .pipe(takeUntil(this.destroyed$))
         .subscribe((value: ExtensionOption | null) => {
            setNullActionValue(value, lastRouteNoAnswerValue, routeNoAnswerExtension, routeNoAnswerNullAction);
            this.routeNoAnswerPlaceholder = getNullActionPlaceholder(routeNoAnswerNullAction?.value);
         });
   }

   handleRouteBusyExtensionChange() {
      const selectedRouteBusyExtension = this.form.get('routeBusyExtension')?.value;
      const routeBusyVoicemailOnly = this.form.get('isRouteBusyVoicemailOnly');
      if (!selectedRouteBusyExtension) {
         return;
      }
      this.isRouteBusyExtensionTypePhone =
         selectedRouteBusyExtension?.type === ExtensionTypes.Phone && selectedRouteBusyExtension?.extensionId >= 0;
      if (!this.isRouteBusyExtensionTypePhone) {
         routeBusyVoicemailOnly?.setValue(false);
         routeBusyVoicemailOnly?.markAsDirty();
         routeBusyVoicemailOnly?.updateValueAndValidity();
      }
   }
   handleRouteNoAnswerExtensionChange() {
      const selectedRouteNoAnswerExtension = this.form.get('routeNoAnswerExtension')?.value;
      const routeNoAnswerVoicemailOnly = this.form.get('isRouteNoAnswerVoicemailOnly');
      if (!selectedRouteNoAnswerExtension) {
         return;
      }
      this.isRouteNoAnswerExtensionTypePhone =
         selectedRouteNoAnswerExtension?.type === ExtensionTypes.Phone &&
         selectedRouteNoAnswerExtension?.extensionId >= 0;
      if (!this.isRouteNoAnswerExtensionTypePhone) {
         routeNoAnswerVoicemailOnly?.setValue(false);
         routeNoAnswerVoicemailOnly?.markAsDirty();
         routeNoAnswerVoicemailOnly?.updateValueAndValidity();
      }
   }
   trackBy(item: ExtensionOption | null) {
      return item?.extensionId;
   }

   ngDoCheck(): void {
      if (this.parentForm.form.invalid && (this.parentForm.submitted || this.form.touched)) {
         this.form.markAllAsTouched();
         this.changeDetectorRef.markForCheck();
      }
   }
}
